import {
  Button,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Table,
  Tag,
  Spin,
  Avatar,
  Typography,
} from "antd";
import React, { useMemo, useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { DeleteIcon, EditIcon } from "../../../../../assets/svg";
import { colors } from "../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes, userRoles } from "../../../../../constants";
import { baseRouteMaker } from "../../../../../constants/routeConsts";
import Actions from "../../../../../redux/actions";
import StaffDeleteDialog from "./StaffDeleteDialog";
import { getStaffAction } from "../../../../../redux/services/staff/staffServices";
import Spinner from "../../../../common/spinner/Spinner";
import "./Staff.scss";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../hooks/useDebounce";
import { UserOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { getUserProfileAction } from "../../../../../redux/services/auth/authServices";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../constants/config/appConfig";
import StaffRenewalDialog from "./StaffRenewalDialog";

const StaffList = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  const {setUserSession} = useLocalStorage(appConfig.SESSION_USER_INFO)

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { staffs } = useSelector((state) => state.staff);
  const users = useSelector(
    (state) => state?.auth?.loginDetails?.organization_id
  );
  const [searchValue] = useState("");
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: staffs?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openStaffDeleteDialog, setOpenStaffDeleteDialog] = useState(false);
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getStaffAction(dataToBeSent, setLoading));
    /* setting the user session based on roles */
    dispatch(getUserProfileAction(setUserSession));
    /* setting the user session based on roles */
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("staffList.name"),
        key: "name",
        render: (_, { profile_image }) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                src={profile_image}
                key={profile_image}
                icon={<UserOutlined />}
              />
              {`${_.first_name} ${_.last_name}`}
            </Flex>
          );
        },
      },
      {
        title: t("staffList.staff_type"),
        dataIndex: "role",
        key: "role",
        render: (_, { role }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background:
                  role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                    ? colors.whiteSmoke
                    : role === "medical_staff"
                    ? colors.lightPastelBlue
                    : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                    ? colors.darkSearGreen
                    : "",
                color:
                  role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                    ? colors.darkGrey
                    : role === "medical_staff"
                    ? colors.oceanBlue
                    : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                    ? colors.limeGreen
                    : "",
              }}
            >
              {role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                ? "Regular"
                : role === "medical_staff"
                ? "Medical"
                : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                ? "Doctor"
                : ""}
            </Tag>
          </>
        ),
      },
      {
        title: t("staffList.email"),
        dataIndex: "email",
        key: "email",
      },
      {
        title: t("staffList.phone"),
        dataIndex: "phone",
        key: "phone",
        render: (phone) => `+${phone?.country_code}-${phone?.phone_number}`,
      },
      {
        title: t("staffList.availability"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "active":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "inactive":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "suspended":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "deleted":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              default:
                break;
            }
          };

          const getStatusNameInArabicAsWell = (status) => {
            switch (status) {
              case "active":
                return t("CommonUtils.active");

              case "inactive":
                return t("CommonUtils.inactive")

              case "suspended":
                return t("CommonUtils.suspended")

              case "deleted":
                return t("CommonUtils.delete")

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                {getStatusNameInArabicAsWell(record?.status)}
              </Tag>
            </>
          );
        },
      },
      {
        title: (
          <>
            <Flex gap="1rem" align="center" justify="center">
              <>{t("Modules.Offer.OfferForm.renewal")}</>
            </Flex>
          </>
        ),
        key: "renewal",
        dataIndex: "renewal",
        align: "center",
        render: (_, record) => (
          <>
            <Button
              type="primary"
              size="small"
              onClick={(e) => handleBuyRenewal(e, record)}
              disabled={
                record?.status === "active"
              }
            >
              {t("Modules.Offer.OfferForm.renew")}
            </Button>
          </>
        ),
      },
      {
        title: t("staffList.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
            {
              record?.status === "active" ?
              <Flex gap="small" justify="center">
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<EditIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleAddEditDetails(e, record)}
                  />
                </Space>
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<DeleteIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleConfirmDelete(e, record)}
                  />
                </Space>
              </Flex>
              :
              <>-</>
            }
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId: "" })
    );
    navigate(baseRouteMaker(routes.CREATE_STAFF));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedStaffId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId })
      );
    }
  };

  

  const handleBuyRenewal = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId: "" })
    );
    setStaffRenewalDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedStaffId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId })
      );
    }
  };

  const handleConfirmDelete = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId: "" })
    );
    setOpenStaffDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedStaffId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_STAFF_ID, { selectedStaffId })
      );
    }
  };

  const isOrgVerified = useSelector(
    (state) => state?.auth?.loginDetails?.organization_id?.organization_verified
  );

  const openVerificationDialog = () => {
    dispatch(Actions.createAction(Actions.VERIFIED_DIALOG_OPEN));
  };

  const [staffRenewalDialog, setStaffRenewalDialog] = useState(false);


  const staffRenewalDialogProps = useMemo(() => ({
    open : staffRenewalDialog,
    onClose : () => {
      setStaffRenewalDialog(false)
    }
  }),[staffRenewalDialog])

  return (
    <div className="staffListContainer">
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Title level={2}>{t("staffList.staff")}</Title>
          <Button
            type="primary"
            onClick={(e) =>
              isOrgVerified
                ? handleAddEditDetails(e, null)
                : openVerificationDialog()
            }
          >
            {t("staffList.addStaff")}
          </Button>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Flex gap="1rem">
              <Card
                bordered
                hoverable
                style={{
                  width: 300,
                  padding: 0,
                  borderRadius: 20,
                  border: `1px solid ${colors.oceanBlue}`,
                }}
              >
                <Row gutter={[24, 24]} align="middle" justify="center">
                  <Col span={6}>
                    <Button
                      type="primary"
                      shape="circle"
                      size="middle"
                      icon={<UserOutlined style={{ fontSize: 22 }} />}
                      style={{ background: colors.oceanBlue }}
                    />
                  </Col>
                  <Col span={18}>
                    <Flex vertical>
                      <Typography.Title level={3}>
                        {t("staffList.planStaffMembers")}
                      </Typography.Title>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color:colors.DarkMint }}
                      >
                        {users?.free_users_created} /{" "}
                        {users?.free_users_allowed}
                      </Typography.Title>
                    </Flex>
                  </Col>
                </Row>
              </Card>
              <Card
                bordered
                hoverable
                style={{
                  width: 300,
                  padding: 0,
                  borderRadius: 20,
                  border: `1px solid ${colors.oceanBlue}`,
                }}
              >
                <Row gutter={[24, 24]} align="middle" justify="center">
                  <Col span={6}>
                    <Button
                      type="primary"
                      shape="circle"
                      size="middle"
                      icon={<UsergroupAddOutlined style={{ fontSize: 22 }} />}
                      style={{ background: colors.oceanBlue }}
                    />
                  </Col>
                  <Col span={18}>
                    <Flex vertical>
                      <Typography.Title level={3}>
                      {t("staffList.addonStaffMembers")}
                      </Typography.Title>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: colors.DarkMint }}
                      >
                        {users?.paid_users_created} /{" "}
                        {users?.paid_users_allowed}
                        {/* {staffs.total} */}
                      </Typography.Title>
                    </Flex>
                  </Col>
                </Row>
              </Card>
            </Flex>
            <Table
              dataSource={staffs?.data}
              columns={columns}
              pagination={paginationObj}
            />
          </>
        )}
      </Flex>
      <StaffDeleteDialog
        open={openStaffDeleteDialog}
        onClose={() => {
          setOpenStaffDeleteDialog(false);
          handleGetTableData();
        }}
      />
      <StaffRenewalDialog {...staffRenewalDialogProps}
      />
    </div>
  );
};

export default StaffList;
